import React, { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import { WishTobacco, RetailerSource, supabase, Tobacco } from '../lib/supabase';
import RetailerLinks from './RetailerLinks';

interface WishListCardProps {
  item: WishTobacco;
  retailers: RetailerSource[];
  onEdit: (item: WishTobacco) => void;
  onDelete: (id: string) => Promise<void>;
  onPurchase: (item: WishTobacco) => void;
}

export default function WishListCard({ item, retailers, onEdit, onDelete, onPurchase }: WishListCardProps) {
  const [blendDetails, setBlendDetails] = useState<Tobacco | null>(null);
  const [loadingBlend, setLoadingBlend] = useState(false);

  const detailBrand = blendDetails?.brand ?? item.brand;
  const detailName = blendDetails?.name ?? item.blend_name;
  const detailAvgRating = blendDetails?.avg_rating ?? null;
  const detailReviewCount = blendDetails?.review_count ?? null;
  const detailBlendType = blendDetails?.blend_type ?? null;
  const detailFlavorNotes = blendDetails?.flavor_notes ?? blendDetails?.flavor_tags ?? null;
  const detailDescription = blendDetails?.description ?? null;

  // Load blend details if this wish item has a matched blend
  useEffect(() => {
    if (item.blend_ref_id) {
      loadBlendDetails();
    }
  }, [item.blend_ref_id]);

  async function loadBlendDetails() {
    if (!item.blend_ref_id) return;
    
    setLoadingBlend(true);
    try {
      const { data, error } = await supabase
        .from('tobaccos')
        .select('id, brand, name, avg_rating, review_count, blend_type, flavor_notes, description, flavor_tags')
        .eq('id', item.blend_ref_id)
        .single();

      if (!error && data) {
        setBlendDetails(data as unknown as Tobacco);
        return;
      }

      // Fallback for legacy wish items still pointing to blend_reference
      const { data: legacyData } = await supabase
        .from('blend_reference')
        .select('id, brand, blend_name, avg_rating, review_count, blend_type, flavor_notes, description, manufacturer, origin_country, strength, cut, image_url')
        .eq('id', item.blend_ref_id)
        .single();

      if (legacyData) {
        setBlendDetails({
          id: legacyData.id,
          name: legacyData.blend_name,
          type: 'pipe',
          brand: legacyData.brand,
          flavor_tags: legacyData.flavor_notes ?? [],
          strength: (legacyData.strength as string | null) ?? undefined,
          origin: legacyData.origin_country ?? undefined,
          image_url: legacyData.image_url ?? undefined,
          cut: (legacyData.cut as string | null) ?? undefined,
          created_at: item.created_at,
          avg_rating: legacyData.avg_rating,
          review_count: legacyData.review_count,
          blend_type: legacyData.blend_type,
          flavor_notes: legacyData.flavor_notes,
          description: legacyData.description,
          manufacturer: legacyData.manufacturer ?? null,
          origin_country: legacyData.origin_country ?? null,
        } as Tobacco);
      }
    } catch (err) {
      console.error('Error loading blend details:', err);
    } finally {
      setLoadingBlend(false);
    }
  }

  return (
    <div className="card" style={{ 
      display: 'flex', 
      flexDirection: 'column',
      gap: '0.75rem' 
    }}>
      {/* Main content row */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        {/* Image */}
        {item.image_url ? (
          <img 
            src={item.image_url} 
            alt={item.blend_name} 
            loading="lazy" 
            style={{ 
              width: '80px', 
              height: '80px', 
              objectFit: 'cover', 
              borderRadius: '8px', 
              flexShrink: 0 
            }} 
          />
        ) : (
          <div style={{ 
            width: '80px', 
            height: '80px', 
            backgroundColor: '#1f1f1f', 
            borderRadius: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexShrink: 0 
          }}>
            <Package size={32} color="#666" />
          </div>
        )}
        
        {/* Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Basic info */}
          <div>
            <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.25rem', color: '#ffffff' }}>
              {item.brand}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-accent)', marginBottom: '0.25rem' }}>
              {item.blend_name}
            </div>
            
            {/* Enhanced blend details (if matched) */}
            {blendDetails && (
              <div style={{ marginTop: '0.5rem' }}>
                {/* Rating */}
                {detailAvgRating && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#cccccc', 
                    marginBottom: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    ⭐ {detailAvgRating.toFixed(1)}
                    {detailReviewCount && (
                      <span style={{ opacity: 0.7 }}>
                        ({detailReviewCount} reviews)
                      </span>
                    )}
                  </div>
                )}

                {/* Blend type badge */}
                {detailBlendType && (
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    backgroundColor: 'rgba(211, 222, 94, 0.15)',
                    border: '1px solid var(--color-accent)',
                    borderRadius: '4px',
                    color: 'var(--color-accent)',
                    marginBottom: '0.5rem'
                  }}>
                    {detailBlendType}
                  </span>
                )}

                {/* Flavor notes */}
                {detailFlavorNotes && detailFlavorNotes.length > 0 && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#cccccc',
                    marginTop: '0.5rem' 
                  }}>
                    {detailFlavorNotes.join(' • ')}
                  </div>
                )}

                {/* Description */}
                {detailDescription && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#999',
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    {detailDescription}
                  </div>
                )}
              </div>
            )}
            
            {/* User notes */}
            {item.notes && (
              <div style={{ 
                fontSize: '0.75rem', 
                color: '#cccccc', 
                marginTop: '0.5rem',
                padding: '0.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <strong style={{ color: '#ffffff' }}>Your notes:</strong> {item.notes}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Retailer quick-search links (only for matched blends) */}
      {blendDetails && retailers.length > 0 && (
        <RetailerLinks 
          retailers={retailers}
          brand={detailBrand}
          blendName={detailName}
        />
      )}

      {/* Action buttons */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        gap: '0.5rem' 
      }}>
        <button 
          onClick={() => onEdit(item)} 
          style={{ 
            padding: '0.4rem 0.75rem', 
            fontSize: '0.75rem', 
            backgroundColor: 'var(--color-card)', 
            border: '1px solid var(--color-accent)', 
            color: 'var(--color-accent)', 
            borderRadius: '6px', 
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
            e.currentTarget.style.color = 'var(--color-background)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-card)';
            e.currentTarget.style.color = 'var(--color-accent)';
          }}
        >
          ✏️ Edit
        </button>
        <button 
          onClick={async () => { await onDelete(item.id); }} 
          style={{ 
            padding: '0.4rem 0.75rem', 
            fontSize: '0.75rem', 
            backgroundColor: 'var(--color-card)', 
            border: '1px solid var(--color-danger)', 
            color: 'var(--color-danger)', 
            borderRadius: '6px', 
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e4574d';
            e.currentTarget.style.color = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-card)';
            e.currentTarget.style.color = 'var(--color-danger)';
          }}
        >
          🗑️ Delete
        </button>
        <button 
          onClick={() => onPurchase(item)} 
          style={{ 
            padding: '0.4rem 0.75rem', 
            fontSize: '0.75rem', 
            backgroundColor: 'var(--color-accent)', 
            border: 'none', 
            color: '#0b0b0b', 
            borderRadius: '6px', 
            cursor: 'pointer', 
            fontWeight: 700,
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = '#0b0b0b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
            e.currentTarget.style.color = '#0b0b0b';
          }}
        >
          ✓ Purchased →
        </button>
      </div>
    </div>
  );
}
